import React, { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { useAuth } from "src/contexts/AuthContext";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import {
  validateReferralCode,
  applyReferralCode,
} from "src/services/referralCodeService";

// Sample list of universities
const universities = [
  "Harvard University",
  "Stanford University",
  "Massachusetts Institute of Technology",
  "University of California, Berkeley",
  "University of Oxford",
  "California Institute of Technology",
  "University of Cambridge",
  "Princeton University",
  "Yale University",
  "Columbia University",
];

interface SurveyOption {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface SurveyStep {
  id: string;
  question: string;
  description: string;
  options?: SurveyOption[];
  type?: "multiple-choice" | "user-info" | "multiple-select";
  fields?: {
    id: string;
    label: string;
    placeholder: string;
    required?: boolean;
    type?: "text" | "university";
  }[];
  maxSelections?: number;
}
const socialMediaStep: SurveyStep = {
  id: "socialMediaPlatform",
  type: "multiple-choice",
  question: "Which social media platform did you find us on?",
  description: "Help us understand which platform brought you here",
  options: [
    {
      id: "linkedin",
      icon: "🔗",
      title: "LinkedIn",
      description: "Professional networking platform",
    },
    {
      id: "reddit",
      icon: "👽",
      title: "Reddit",
      description: "Social news and discussion platform",
    },
    {
      id: "xiaohongshu",
      icon: "📕",
      title: "Xiaohongshu",
      description: "Chinese lifestyle and shopping platform",
    },
    {
      id: "instagram",
      icon: "📸",
      title: "Instagram",
      description: "Photo and video sharing platform",
    },
    {
      id: "others",
      icon: "✨",
      title: "Others",
      description: "Other social media platforms",
    },
  ],
};
const SurveyPage = () => {
  const navigate = usePartnerAwareNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selections, setSelections] = useState<
    Record<string, string | string[]>
  >({});

  const [userInfo, setUserInfo] = useState({
    name: "",
    university: "",
    referralCode: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUniversities, setFilteredUniversities] = useState<string[]>(
    []
  );
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [fromSocialMedia, setFromSocialMedia] = useState(-1);

  // Referral code validation states
  const [referralCodeValidation, setReferralCodeValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    message: string;
    interviewCount?: number;
    willIncrease?: boolean;
  }>({
    isValidating: false,
    isValid: null,
    message: "",
  });
  const surveySteps: SurveyStep[] = [
    {
      id: "userInfo",
      question: "Tell us about yourself",
      description: "Help us personalize your preparation experience",
      type: "user-info",
      fields: [
        {
          id: "name",
          label: "Full Name",
          placeholder: "Enter your full name",
          required: true,
          type: "text",
        },
      ],
    },
    {
      id: "referralCode",
      question: "Have a referral code?",
      description: `Enter a referral code to get additional free interviews. Valid codes will increase your interview count if it's higher than your current count.`,
      type: "user-info",
      fields: [
        {
          id: "referralCode",
          label: "Referral Code (Optional)",
          placeholder: "Enter your referral code",
          required: false,
          type: "text",
        },
      ],
    },
    {
      id: "referralSource",
      type: "multiple-select",
      question: "How did you hear about us?",
      maxSelections: 2,
      description: "Help us understand how you found our platform",
      options: [
        {
          id: "friend",
          icon: "👥",
          title: "Friend/Classmate",
          description: "Recommended by someone you know",
        },
        {
          id: "socialMedia",
          icon: "📱",
          title: "Social Media",
          description:
            "Found us through LinkedIn, Instagram, or other platforms",
        },
        {
          id: "club",
          icon: "🎯",
          title: "Professional Club",
          description: "Through your university's career services",
        },
        {
          id: "search",
          icon: "🔍",
          title: "Online Search",
          description: "Found us through Google or other search engines",
        },
        {
          id: "other",
          icon: "✨",
          title: "Other",
          description: "Found us through other channels",
        },
      ],
    },

    socialMediaStep,
    {
      id: "casingExperience",
      type: "multiple-choice",
      question: "Have you done case interviews before?",
      description: "Tell us about your previous case interview experience",
      options: [
        {
          id: "noExperience",
          icon: "🆕",
          title: "No Experience",
          description: "Never participated in a case interview before",
        },
        {
          id: "selfPractice",
          icon: "📚",
          title: "Self Practice Only",
          description: "Practiced cases alone or with materials",
        },
        {
          id: "peerPractice",
          icon: "👥",
          title: "Peer Practice",
          description: "Practiced with friends or study groups",
        },
        {
          id: "realInterview",
          icon: "🎯",
          title: "Real Interview Experience",
          description: "Had actual consulting case interviews before",
        },
      ],
    },
    {
      id: "targetFirms",
      type: "multiple-choice",
      question: "Which consulting firms are you targeting?",
      description: "Select your primary target for case interview preparation",
      options: [
        {
          id: "mbb",
          icon: "🎯",
          title: "MBB (McKinsey, Bain, BCG)",
          description: "Preparing for top-tier strategy consulting firms",
        },
        {
          id: "big4",
          icon: "💼",
          title: "Big 4 Consulting",
          description: "Targeting Deloitte, EY, KPMG, PwC consulting roles",
        },
        {
          id: "boutique",
          icon: "⚡",
          title: "Boutique Consulting",
          description: "Focusing on specialized or boutique consulting firms",
        },
        {
          id: "exploring",
          icon: "🔍",
          title: "Still Exploring",
          description: "Learning about different consulting opportunities",
        },
      ],
    },
    {
      id: "focusArea",
      type: "multiple-select",
      question: "What areas do you want to focus on?",
      description: "Select up to 4 areas you want to improve",
      maxSelections: 4,
      options: [
        {
          id: "structure",
          icon: "🏗️",
          title: "Problem Structuring",
          description: "Breaking down complex problems systematically",
        },
        {
          id: "math",
          icon: "🔢",
          title: "Mental Math & Analysis",
          description: "Improving calculation speed and accuracy",
        },
        {
          id: "communication",
          icon: "🗣️",
          title: "Communication",
          description: "Presenting thoughts clearly and confidently",
        },
        {
          id: "frameworks",
          icon: "📋",
          title: "Framework Application",
          description: "Using consulting frameworks effectively",
        },
      ],
    },
    {
      id: "experience",
      question: "What's your case interview experience?",
      description: "Tell us about your current preparation level",
      options: [
        {
          id: "beginner",
          icon: "🌱",
          title: "Just Starting",
          description: "New to case interviews and consulting frameworks",
        },
        {
          id: "learning",
          icon: "📚",
          title: "Learning Fundamentals",
          description: "Familiar with basics, completed a few practice cases",
        },
        {
          id: "practicing",
          icon: "🎯",
          title: "Regular Practice",
          description: "Completed 10+ cases, working on consistency",
        },
        {
          id: "advanced",
          icon: "🚀",
          title: "Interview Ready",
          description: "Extensive practice, fine-tuning performance",
        },
      ],
    },
    {
      id: "schedule",
      question: "When is your first interview?",
      description: "Help us customize your practice schedule",
      options: [
        {
          id: "urgent",
          icon: "⚡",
          title: "Within 2 weeks",
          description: "Need intensive preparation immediately",
        },
        {
          id: "month",
          icon: "📅",
          title: "1-2 months away",
          description: "Time for structured, regular practice",
        },
        {
          id: "planning",
          icon: "🎯",
          title: "3+ months away",
          description: "Building long-term preparation strategy",
        },
        {
          id: "exploring",
          icon: "🔍",
          title: "No specific timeline",
          description: "Exploring and building skills gradually",
        },
      ],
    },
  ];
  const { firebaseUser, userDoc } = useAuth();
  useEffect(() => {
    if (userDoc) {
      if (userDoc.finishSurvey) {
        navigate(`/`);
      }
    }
  }, [userDoc]);

  // Function to validate referral code
  const validateReferralCodeLocal = async (code: string) => {
    if (!code || code.trim() === "") {
      setReferralCodeValidation({
        isValidating: false,
        isValid: null,
        message: "",
      });
      return;
    }

    setReferralCodeValidation({
      isValidating: true,
      isValid: null,
      message: "Validating referral code...",
    });

    try {
      const result = await validateReferralCode(code.trim(), firebaseUser?.uid);

      setReferralCodeValidation({
        isValidating: false,
        isValid: result.valid,
        message: result.message,
        interviewCount: result.interviewCount,
        willIncrease: result.willIncreaseCount,
      });
    } catch (error: any) {
      console.error("Error validating referral code:", error);
      setReferralCodeValidation({
        isValidating: false,
        isValid: false,
        message: error.message || "Invalid referral code",
      });
    }
  };

  // Debounced referral code validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (userInfo.referralCode) {
        validateReferralCodeLocal(userInfo.referralCode);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [userInfo.referralCode, firebaseUser?.uid]);
  const currentStep = surveySteps[currentStepIndex];
  const progress = ((currentStepIndex + 1) / surveySteps.length) * 100;

  const handleOptionSelect = (optionId: string) => {
    if (currentStep.type === "multiple-select") {
      const currentSelections = Array.isArray(selections[currentStep.id])
        ? (selections[currentStep.id] as string[])
        : [];
      let newSelections: string[];

      if (currentSelections.includes(optionId)) {
        // Remove the option if it's already selected
        newSelections = currentSelections.filter((id) => id !== optionId);
      } else if (
        currentSelections.length < (currentStep.maxSelections || Infinity)
      ) {
        // Add the option if we haven't reached max selections
        newSelections = [...currentSelections, optionId];
      } else {
        return; // Max selections reached
      }

      setSelections((prev) => ({
        ...prev,
        [currentStep.id]: newSelections,
      }));
    } else {
      setSelections((prev) => ({
        ...prev,
        [currentStep.id]: optionId,
      }));
    }
  };

  const isOptionSelected = (optionId: string): boolean => {
    const currentSelection = selections[currentStep.id];
    if (Array.isArray(currentSelection)) {
      return currentSelection.includes(optionId);
    }
    return currentSelection === optionId;
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === "university") {
      setSearchTerm(value);
      setUserInfo((prev) => ({
        ...prev,
        [field]: value,
      }));

      if (value.length > 0) {
        const filtered = universities.filter((university) =>
          university.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredUniversities(filtered);
        setIsDropdownVisible(true);
      } else {
        setFilteredUniversities([]);
        setIsDropdownVisible(false);
      }
    } else if (field === "referralCode") {
      // Capitalize referral code input
      const capitalizedValue = value.toUpperCase();
      setUserInfo((prev) => ({
        ...prev,
        [field]: capitalizedValue,
      }));
    } else {
      setUserInfo((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleUniversitySelect = (university: string) => {
    setUserInfo((prev) => ({
      ...prev,
      university: university,
    }));
    setSearchTerm(university);
    setFilteredUniversities([]);
    setIsDropdownVisible(false);
  };

  const isCurrentStepValid = () => {
    if (currentStep.type === "user-info") {
      return (
        currentStep.fields?.every((field) =>
          field.required
            ? userInfo[field.id as keyof typeof userInfo].trim() !== ""
            : true
        ) ?? false
      );
    }
    if (currentStep.type === "multiple-select") {
      const currentSelections = (selections[currentStep.id] as string[]) || [];
      return currentSelections.length > 0;
    }
    return selections[currentStep.id] !== undefined;
  };

  const getRemainingSelections = (): number => {
    if (currentStep.type === "multiple-select" && currentStep.maxSelections) {
      const currentSelections = (selections[currentStep.id] as string[]) || [];
      return currentStep.maxSelections - currentSelections.length;
    }
    return 0;
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      // Check if we're at the social media step and need to skip it when going back
      if (currentStepIndex === 3 && fromSocialMedia === 0) {
        setCurrentStepIndex(1); // Go back to referral source step
        return;
      }
      setCurrentStepIndex((prev) => Math.max(0, prev - 1));
    }
  };

  const handleNext = async () => {
    if (currentStepIndex < surveySteps.length - 1) {
      // Check if the current step is the referral source step
      if (currentStep.id === "referralSource") {
        const referralSelections = selections[currentStep.id] as string[];
        if (referralSelections?.includes("socialMedia")) {
          setFromSocialMedia(1);
          setCurrentStepIndex((prev) => prev + 1);
        } else {
          setFromSocialMedia(0);
          // Skip the social media step
          setCurrentStepIndex((prev) => prev + 2);
        }
        return;
      }
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      // Complete the survey and apply referral code if provided
      if (!firebaseUser?.uid) {
        console.error("No user ID available");
        return;
      }

      try {
        const result = await applyReferralCode(
          firebaseUser.uid,
          userInfo.referralCode,
          {
            ...userInfo,
            ...selections,
          }
        );

        if (result.success) {
          // Show success message if referral code was applied
          if (result.interviewCountUpdated) {
            // You could show a success toast here
            console.log("Referral code applied successfully!");
          }
          navigate("/");
        } else {
          console.error("Error completing survey:", result.message);
          // Still navigate to home even if there was an error
          navigate("/");
        }
      } catch (error) {
        console.error("Error completing survey:", error);
        // Still navigate to home even if there was an error
        navigate("/");
      }
    }
  };

  const renderStepContent = () => {
    if (currentStep.type === "user-info") {
      return (
        <div className="space-y-6">
          {currentStep.fields?.map((field) => (
            <div key={field.id} className="space-y-2 relative">
              <label
                htmlFor={field.id}
                className="block text-sm font-medium text-gray-700"
              >
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type === "university" ? (
                <div className="relative">
                  <input
                    type="text"
                    id={field.id}
                    value={searchTerm}
                    onChange={(e) =>
                      handleInputChange(field.id, e.target.value)
                    }
                    placeholder={field.placeholder}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoComplete="off"
                  />
                  {isDropdownVisible && filteredUniversities.length > 0 && (
                    <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {filteredUniversities.map((university, index) => (
                        <li
                          key={index}
                          className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                            index % 2 === 0 ? "bg-gray-50" : "bg-white"
                          }`}
                          onClick={() => handleUniversitySelect(university)}
                        >
                          {university}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    id={field.id}
                    value={userInfo[field.id as keyof typeof userInfo] || ""}
                    onChange={(e) =>
                      handleInputChange(field.id, e.target.value)
                    }
                    placeholder={field.placeholder}
                    className={`w-full px-4 py-3.5 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg pr-12 ${
                      field.id === "referralCode" &&
                      referralCodeValidation.isValid !== null
                        ? referralCodeValidation.isValid
                          ? "border-green-300 bg-green-50"
                          : "border-red-300 bg-red-50"
                        : "border-gray-200"
                    }`}
                  />
                  {field.id === "referralCode" && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {referralCodeValidation.isValidating ? (
                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : referralCodeValidation.isValid === true ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : referralCodeValidation.isValid === false ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : null}
                    </div>
                  )}
                </div>
              )}
              {field.id === "referralCode" &&
                referralCodeValidation.message && (
                  <div
                    className={`mt-2 text-sm flex items-center gap-2 ${
                      referralCodeValidation.isValid === true
                        ? "text-green-600"
                        : referralCodeValidation.isValid === false
                        ? "text-red-600"
                        : "text-blue-600"
                    }`}
                  >
                    {referralCodeValidation.isValid === true ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : referralCodeValidation.isValid === false ? (
                      <XCircle className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    <span>{referralCodeValidation.message}</span>
                  </div>
                )}
            </div>
          ))}
        </div>
      );
    }

    const remainingSelections = getRemainingSelections();

    return (
      <>
        {currentStep.type === "multiple-select" &&
          currentStep.maxSelections && (
            <div className="mb-4 text-center text-sm text-gray-600">
              {remainingSelections === 0 ? (
                <span>Maximum selections reached</span>
              ) : (
                <span>
                  Select up to {remainingSelections} more{" "}
                  {remainingSelections === 1 ? "area" : "areas"}
                </span>
              )}
            </div>
          )}
        <div className="space-y-4">
          {currentStep.options?.map((option) => (
            <button
              key={option.id}
              onClick={() => handleOptionSelect(option.id)}
              disabled={
                currentStep.type === "multiple-select" &&
                !isOptionSelected(option.id) &&
                remainingSelections === 0
              }
              className={`w-full p-4 rounded-xl border-2 text-left transition-all
                                ${
                                  isOptionSelected(option.id)
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-gray-300"
                                }
                                ${
                                  currentStep.type === "multiple-select" &&
                                  !isOptionSelected(option.id) &&
                                  remainingSelections === 0
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                }
                            `}
            >
              <div className="flex items-center gap-4">
                <span className="text-2xl">{option.icon}</span>
                <div>
                  <h3 className="font-semibold text-lg">{option.title}</h3>
                  <p className="text-gray-600">{option.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          {currentStepIndex > 0 && (
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
          )}
          <div className="w-full bg-gray-200 h-2 rounded-full mx-4">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto px-4 py-8 mb-16">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-4">{currentStep.question}</h1>
          <p className="text-gray-600">{currentStep.description}</p>
        </div>

        {renderStepContent()}

        <div className="mt-8 pb-4">
          <button
            onClick={handleNext}
            className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-colors
                    ${
                      isCurrentStepValid()
                        ? "bg-blue-500 hover:bg-blue-600"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
            disabled={!isCurrentStepValid()}
          >
            {currentStepIndex === surveySteps.length - 1
              ? "Complete"
              : "Continue"}
          </button>
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 text-center text-gray-600">
          Step {currentStepIndex + 1} of {surveySteps.length}
        </div>
      </footer>
    </div>
  );
};

export default SurveyPage;
